/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './site-map.scss';

/* eslint-disable import/no-unresolved, import/default */

import siteMapTemplate from './site-map.tpl.html';
import addSiteMapAssetTemplate from './add-site-map-asset.tpl.html';

/* eslint-enable import/no-unresolved, import/default */

import 'leaflet/dist/leaflet.css';
import L from 'leaflet/dist/leaflet';
import turfBbox from '@turf/bbox';

export default angular.module('agrolog.siteMap', [])
    .directive('agSiteMap', SiteMap)
    .name;

const maxZoom= 4;
const mapBoxToken= 'pk.eyJ1IjoiamVwcGV3YWx0aGVyIiwiYSI6ImNsZjV1a28wOTFnM2Mzem56M2h0dTh0dGcifQ._tlpkVov-vf_4Knl-VSzcg';

/*@ngInject*/
function SiteMap() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            hasParentAsset: '=',
            parentAssetId: '=',
            fetchMapAssets: '&',
            onAssetSelected: '&'
        },
        controller: SiteMapController,
        controllerAs: 'vm',
        templateUrl: siteMapTemplate
    };
}

/* eslint-disable angular/angularelement */

const assetPinSize = 64;

/*@ngInject*/
function SiteMapController($rootScope, $scope, $element, $filter, $log, $timeout, $mdDialog, $compile,
                           $document, $q, $translate, $window, siteService, types, agTypes, agUtils, userService, siloGroupService) {

    let vm = this;

    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' ||
        userService.getAuthority() === 'CUSTOMER_USER';

    vm.hasData = false;

    vm.aspect = 0;
    vm.height = 0;
    vm.width = 0;

    vm.siteAssets = [];
    vm.allSiteAssets = [];
    vm.site = null;
    vm.isClientMode = true;
    vm.inEditMode = false;
    vm.mode = 'site';
    vm.loading = false;

    // Initial zoom data
    vm.zoomInitialized = false;
    vm.assetsCoordinates = [];
    vm.currentPosition = null;

    vm.addSiteAssetAction = {
        name: 'ag.site.add-site-asset',
        show: true,
        onAction: function($event) {
            vm.addSiteAsset($event);
        },
        icon: 'add'
    };

    vm.aerationConnectedSystemStatusSubscriptionOption = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError
        },
        useDashboardTimewindow: false
    };

    vm.helpPageAction = agUtils.createHelpActionConfig("agGuiComponents", "ag.docsPageSections.assets-widget");

    vm.toggleEditMode = function() {
        vm.inEditMode = !vm.inEditMode;
    }

    vm.addSiteAsset = addSiteAsset;
    vm.removeSiteAsset = removeSiteAsset;
    vm.onSiteAssetMouseDown = onSiteAssetMouseDown;
    vm.getAssetStateIcon = getAssetStateIcon;
    vm.getAssetStateAnimation = getAssetStateAnimation;
    vm.siteAssetTooltipColor = siteAssetTooltipColor;

    $scope.$watch('vm.inEditMode', function () {
        updateMarkers();
    });

    $scope.$watch('vm.allSiteAssets + vm.siteAssets', function () {

        if (vm.isClientMode) {
            const availableSiteAssets = getAssetsWithoutLocation();

            if (availableSiteAssets.length > 0) {
                addSiteAsset(null, true);
                vm.inEditMode = true;
            }
        }
    });

    $scope.$watch('vm.ctx', function() {
        if (vm.showData && vm.ctx) {
            vm.isEdit = vm.ctx.isEdit;
            if (vm.ctx.settings) {
                if (angular.isDefined(vm.ctx.settings.isClientMode)) {
                    vm.isClientMode = vm.ctx.settings.isClientMode;
                }
                if (vm.hasParentAsset) {
                    vm.mode = 'site';
                } else {
                    vm.mode = angular.isDefined(vm.ctx.settings.mode) ? vm.ctx.settings.mode : 'site';
                }
            }
            vm.ctx.$containerParent.css('overflow', 'visible');
            if (vm.hasParentAsset) {
                vm.ctx.widgetActions = [ vm.helpPageAction ];
            } else {
                vm.ctx.widgetActions = [ vm.addSiteAssetAction, vm.helpPageAction ];
                vm.addSiteAssetAction.show = !vm.isClientMode;
                if (!vm.isClientMode) {
                    if (vm.mode === 'sites') {
                        vm.ctx.widgetTitle = $translate.instant('ag.site.sites-map') + '';
                    } else if (vm.mode === 'site') {
                        vm.ctx.widgetTitle = $translate.instant('ag.site.site-map') + '';
                    } else if (vm.mode === 'siloGroup') {
                        vm.ctx.widgetTitle = $translate.instant('ag.silo-group.silo-group-map') + '';
                    }
                }
            }
            vm.clientParentAssetId = null;
            var currentSiteAssetId;
            if (vm.ctx.stateController) {
                if (vm.hasParentAsset) {
                    loadClientSiteAssets();
                    $scope.$watch('vm.parentAssetId', (newVal, prevVal) => {
                        if (newVal && !angular.equals(newVal, prevVal)) {
                            loadClientSiteAssets();
                        }
                    });
                } else if (vm.isClientMode) {
                    loadClientSiteAssets();
                    $scope.$watch(() => getClientParentAssetId(), (newVal, prevVal) => {
                        if (newVal && !angular.equals(newVal, prevVal)) {
                            loadClientSiteAssets();
                        }
                    });
                } else {
                    vm.clientParentAssetId = vm.ctx.stateController.getStateParams().entityId;
                    if (vm.mode === 'sites') {
                        currentSiteAssetId = vm.clientParentAssetId;
                    }
                    loadSite(currentSiteAssetId);
                }
            }
        }
    });

    $scope.$watch('vm.activeSiteAsset', () => {
        updateMarkersLook();
    });

    $scope.$watch('vm.assetsCoordinates', () => {
        if (vm.assetsCoordinates.length > 0) {
            if (!vm.zoomInitialized) {

                const locationsGeoJson = { features: [], type: 'FeatureCollection' };
                vm.assetsCoordinates.forEach((location) => {
                    const locationItem = {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [location.longitude, location.latitude]
                        }
                    };

                    locationsGeoJson.features.push(locationItem);
                });

                const bbox = turfBbox(locationsGeoJson);
                const minX = bbox[0];
                const minY = bbox[1];
                const maxX = bbox[2];
                const maxY = bbox[3];

                let distance = Math.abs(minX - maxX) / 2;

                const bounds = [
                    [Math.max(minX - distance, -90), Math.max(minY - distance, -90)], // southwestern corner of the bounds
                    [Math.min(maxX + distance, 90), Math.min(maxY + distance, 90)] // northeastern corner of the bounds
                ];

                const areBoundsFinite =
                    Number.isFinite(minX) && Number.isFinite(minY) && Number.isFinite(maxX) && Number.isFinite(maxY);

                if (vm.map) {
                    if (vm.assetsCoordinates.length > 1 && areBoundsFinite) {
                        const corner1 = L.latLng(bounds[0][1], bounds[0][0]);
                        const corner2 = L.latLng(bounds[1][1], bounds[1][0]);
                        vm.map.fitBounds(new L.LatLngBounds(corner1, corner2), { maxZoom: 10 });
                    } else {

                        vm.map.flyTo(new L.LatLng(vm.assetsCoordinates[0].latitude, vm.assetsCoordinates[0].longitude), 8);
                    }
                }

                vm.zoomInitialized = true;
            }
        }
    });

    function getClientParentAssetId() {
        var targetStateId;
        var stateParams;
        if (vm.mode) {
            if (vm.mode === 'site') {
                targetStateId = agTypes.clientState.site;
            } else if (vm.mode === 'siloGroup') {
                targetStateId = agTypes.clientState.siloGroup;
            }
        }
        if (targetStateId) {
            stateParams = vm.ctx.stateController.getStateParamsByStateId(targetStateId);
        }
        if (stateParams) {
            return stateParams.entityId;
        } else {
            return null;
        }
    }

    function loadClientSiteAssets() {
        vm.clientParentAssetId = getClientParentAssetId();
        var currentSiteAssetId;
        if (vm.ctx.stateController.getStateId() !== agTypes.clientState.site) {
            currentSiteAssetId = vm.ctx.stateController.getStateParams().entityId;
        }
        if (vm.mode === 'sites') {
            currentSiteAssetId = vm.clientParentAssetId;
        }
        loadSite(currentSiteAssetId);
    }

    $scope.$on('editModeChanged', function() {
        vm.isEdit = vm.ctx.isEdit;
    });

    $scope.$on('siteSelected', function(event, siteAssetId) {
        siteAssetSelected(siteAssetId);
    });

    $scope.$on('siteAssetSelected', function(event, siteAssetId) {
        siteAssetSelected(siteAssetId);
    });

    function siteAssetSelected(siteAssetId) {
        if (siteAssetId) {
            var result = $filter('filter')(vm.allSiteAssets, {id: { id: siteAssetId.id } }, true);
            if (result && result.length) {
                vm.activeSiteAsset = result[0];
            }
        }
    }

    $scope.$on('siteUpdated', function(event, siteAsset) {
        siteAssetUpdated(siteAsset);
    });

    $scope.$on('siteAssetUpdated', function(event, siteAsset) {
        siteAssetUpdated(siteAsset);
    });

    function siteAssetUpdated(siteAsset) {
        var result = $filter('filter')(vm.allSiteAssets, {id: { id: siteAsset.id.id } }, true);
        if (result && result.length) {
            var prevSiteAsset = result[0];
            var index = vm.allSiteAssets.indexOf(prevSiteAsset);
            if (index > -1) {
                vm.siteAssets[index].name = siteAsset.name;
                vm.siteAssets[index].label = siteAsset.label;
            }
            updateMarkers();
        }
        if (siteAsset.type === agTypes.assetType.aeration) {
            for (let prop in vm.connectedSystemsObj) {
                let connectedSystems = vm.connectedSystemsObj[prop];
                connectedSystems.forEach((aeration) => {
                    if (aeration.id.id === siteAsset.id.id) {
                        aeration.aerationType = (siteAsset.aerationType) ? siteAsset.aerationType : agTypes.aerationType.aeration;
                        aeration.name = siteAsset.name;
                        aeration.label = siteAsset.label;
                    }
                })
            }
        }
    }

    $scope.$on('siteAssetsListUpdated', function(event, clientParentAssetId, keepSelection) {
        if (angular.equals(vm.clientParentAssetId, clientParentAssetId)) {
            reloadSiteAssets(keepSelection);
        }
    });

    $scope.$on('alarmsUpdated', function() {
        reloadSiteAssets(true);
    });

    var testImage = new Image(); // eslint-disable-line no-undef
    testImage.onload = function() {
        vm.aspect = testImage.width / testImage.height;
        onResize();
        /*$timeout(function() {
            vm.loading = false;
        });*/
    }

    var siteMapContainer = angular.element('#site-map-container', $element);
    var siteMap = angular.element('#site-map', $element);

    addResizeListener(siteMapContainer[0], function() { // eslint-disable-line no-undef
        onResize();
    });

    const getCurrentLocation = () => {
        if ($window.navigator.geolocation) {
            $window.navigator.geolocation.getCurrentPosition(
                (position) => {
                    vm.currentPosition = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    }
                },
                () => {
                    vm.currentPosition = null;
                },
                {
                    enableHighAccuracy: true
                }
            );
        }
    }

    function initMap() {
        let settings;
        if(!vm.map) {
            if(vm.aspect > 0) {
                settings =  {
                    minZoom: 1,
                    maxZoom: maxZoom,
                    center: [0, 0],
                    zoom: 1,
                    crs: L.CRS.Simple,
                    attributionControl: false
                }
            } else if (vm.site.useGoogleMaps) {
                settings =  {
                    minZoom: 1,
                    center: [0, 0],
                    zoom: 1,
                    attributionControl: false
                }
            }
            vm.map = L.map(siteMap[0], settings);
            updateBounds();
            updateMarkers();
        }
    }

    function updateBounds() {
        if(!vm.site.useGoogleMaps) {
            var w = vm.width;
            var h = vm.height;
            var southWest = vm.map.unproject([0, h], vm.map.getMaxZoom() - 1);
            var northEast = vm.map.unproject([w, 0], vm.map.getMaxZoom() - 1);
            var bounds = new L.LatLngBounds(southWest, northEast);

            if(vm.imageOverlay) {
                vm.imageOverlay.setBounds(bounds);
            } else {
                vm.imageOverlay = L.imageOverlay(vm.site.imageUrl, bounds).addTo(vm.map);
            }

            var padding = 50 * maxZoom;
            southWest = vm.map.unproject([-padding, h + padding], vm.map.getMaxZoom() - 1);
            northEast = vm.map.unproject([w+padding, -padding], vm.map.getMaxZoom() - 1);
            var maxBounds = new L.LatLngBounds(southWest, northEast);
            vm.map.setMaxBounds(maxBounds);
        } else {
            if(vm.imageOverlay) {
                let bounds = vm.map.getBounds();
                vm.map.fitBounds(bounds, {padding: [50, 50], animate: false});
            } else {
                const mapTile = 'https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=' + mapBoxToken;
                vm.imageOverlay = L.tileLayer(mapTile).addTo(vm.map);
            }
        }
    }

    function updateMarkersLook() {
        if (vm.siteAssetMarkers) {
            for (var id in vm.siteAssetMarkers) {
                var marker = vm.siteAssetMarkers[id];
                if (vm.activeSiteAsset && id === vm.activeSiteAsset.id.id) {
                    angular.element(marker._icon).addClass('ag-active');
                    angular.element(marker._tooltip._container).addClass('ag-active');
                } else {
                    angular.element(marker._icon).removeClass('ag-active');
                    angular.element(marker._tooltip._container).removeClass('ag-active');
                }
            }
        }
    }

    function updateMarkers() {
        if (vm.map) {
            if (vm.siteAssetMarkers) {
                for (var id in vm.siteAssetMarkers) {
                    var marker = vm.siteAssetMarkers[id];
                    vm.map.removeLayer(marker);
                    delete vm.siteAssetMarkers[id];
                }
            }
            if (!vm.siteAssetMarkers) {
                vm.siteAssetMarkers = {};
            }

            const tempCoordinates = [];
            vm.siteAssets.forEach((siteAsset) => {
                vm.siteAssetMarkers[siteAsset.id.id] = createMarker(siteAsset, tempCoordinates);
            });
            vm.assetsCoordinates = tempCoordinates;
            updateMarkersLook();
        }
    }

    function createMarker(siteAsset, tempCoordinates) {
        var divIcon = {
            html: '<img alt="silo" src="' + siteAssetPin(siteAsset) + '" aria-label="silo"'+
            ' class="site-map-asset-image"/>'+
            '<img alt="silo" src="'+siteAssetIcon(siteAsset)+'" aria-label="silo"'+
            ' class="site-map-asset-image"/>',
            className: 'site-map-asset',
            iconSize: [assetPinSize, assetPinSize],
            iconAnchor: [assetPinSize/2, assetPinSize],
            popupAnchor: [0, -assetPinSize]
        };

        var icon = L.divIcon(divIcon);

        var location;
        if(!vm.site.useGoogleMaps) {
            var x = siteAsset.positionX * vm.width;
            var y = siteAsset.positionY * vm.height;
            location = vm.map.unproject([x, y], vm.map.getMaxZoom() - 1);
        } else {
            location = [siteAsset.latitude, siteAsset.longitude];
            tempCoordinates.push({latitude: siteAsset.latitude, longitude: siteAsset.longitude});
        }


        const isMarkerDraggable = vm.inEditMode || !vm.isClientMode;
        const marker = L.marker(location, {icon: icon, draggable: isMarkerDraggable}).addTo(vm.map);
        marker.tooltipOffset = [0, -assetPinSize + 16];

        var tooltipColor = siteAssetTooltipColor(siteAsset);
        var tooltipHtml = '<div style="position: relative; white-space: nowrap; text-align: center; font-size: 16px; top: -8px;">' +
            '<span style="margin-left: -500%;"></span>' +
            '<span style="border: 2px solid '+tooltipColor+'; border-radius: 10px; color: '+tooltipColor+'; background-color: #fff; padding-left: 5px; padding-right: 5px; padding-top: 3px; padding-bottom: 3px;">' + (siteAsset.label || siteAsset.name) + '</span>' +
            '<span style="margin-right: -500%;"></span></div>';
        marker.bindTooltip(tooltipHtml,
            { className: 'ag-marker-label', permanent: true, direction: 'top', offset: marker.tooltipOffset });

        if (vm.isClientMode && !vm.inEditMode) {
            marker.on('click', () => {
                onSiteAssetMouseDown(siteAsset);
            });
            if (siteAsset.connectedSystems) {
                for (let i = 0; i < siteAsset.connectedSystems.length; i++) {
                    let system = siteAsset.connectedSystems[i];
                    if (vm.subscribedConnectedSystems.indexOf(system.id.id) === -1) {
                        vm.subscribedConnectedSystems.push(system.id.id);
                        let systemStatusSubscriptionsInfo = [{
                            type: types.datasourceType.entity,
                            entityType: types.entityType.asset,
                            entityId: system.id.id,
                            entityName: system.name,
                            timeseries: [
                                {name: agTypes.telemetry.aeration.state}
                            ]
                        }];
                        vm.ctx.subscriptionApi.createSubscriptionFromInfo(
                            types.widgetType.latest.value, systemStatusSubscriptionsInfo, vm.aerationConnectedSystemStatusSubscriptionOption, false, true).then(
                            () => {}
                        );
                    }
                }
                if (!vm.connectedSystemsObj[siteAsset.id.id]) {
                    vm.connectedSystemsObj[siteAsset.id.id] = siteAsset.connectedSystems;
                }
                let popupTpl = '<div class="connected-systems-container" layout="column" style="top:' + 22*siteAsset.connectedSystems.length + 'px">' +
                    '<md-button ng-repeat="connectedSystem in vm.connectedSystemsObj[\'' + siteAsset.id.id + '\'] | orderBy:\'name\'" ' +
                        'class="md-icon-button" aria-label="Select connected system" style="border: 2px solid;" ng-style="{\'border-color\': vm.siteAssetTooltipColor(connectedSystem)}" ng-click="vm.onSiteAssetMouseDown(connectedSystem)">' +
                    '<md-icon ng-class="vm.getAssetStateAnimation(connectedSystem, connectedSystem.aerationState)" aria-label="Select connected system" md-svg-icon="{{vm.getAssetStateIcon(connectedSystem)}}" ng-style="{\'fill\': vm.siteAssetTooltipColor(connectedSystem)}"></md-icon>' +
                    '<md-tooltip md-direction="top">' +
                    '{{ connectedSystem.label || connectedSystem.name }}' +
                    '</md-tooltip>' +
                    '</md-button></div>';
                let popupElement = angular.element(popupTpl);
                $compile(popupElement)($scope);
                marker.bindPopup(popupElement[0], {className: 'site-map-asset-toolbar connected-systems', autoClose: false, closeOnClick: false}).openPopup();
                // NOTE: Removed keypress event because it was triggering a popup toggle which does an open/close of the popup when clicking enter.
                marker.off('keypress');
                marker._events.click.pop();
            }
        } else {
            marker.on('mousedown', () => {
                onSiteAssetMouseDown(siteAsset)
            });
            marker.on('dragend', function (event) {
                var marker = event.target;
                var location = marker.getLatLng();
                var position = vm.map.project(location, vm.map.getMaxZoom() - 1);
                if(!vm.site.useGoogleMaps) {
                    siteAsset.positionX = position.x / vm.width;
                    siteAsset.positionY = position.y / vm.height;
                } else {
                    siteAsset.latitude = location.lat;
                    siteAsset.longitude = location.lng;
                }
                saveSiteAsset(siteAsset);
            });
            let popupTpl = '<md-button class="md-icon-button" aria-label="{{ \'ag.site.remove-site-asset\' | translate }}">' +
                '<md-icon aria-label="{{ \'ag.site.remove-site-asset\' | translate }}" class="material-icons">close</md-icon>' +
                '<md-tooltip md-direction="top">' +
                '{{ \'ag.site.remove-site-asset\' | translate }}' +
                '</md-tooltip>' +
                '</md-button>';
            let popupElement = angular.element(popupTpl);
            $compile(popupElement)($scope);
            popupElement.on('click', function ($event) {
                vm.removeSiteAsset($event, siteAsset);
            });
            marker.bindPopup(popupElement[0], {className: 'site-map-asset-toolbar'});
        }

        return marker;
    }

    function onSiteAssetMouseDown(siteAsset) {
        var siteAssetId = siteAsset.id;
        var siteAssetType = siteAsset.type;
        if (siteAssetType === agTypes.assetType.site) {
            $rootScope.$broadcast('siteSelected', siteAssetId);
        } else {
            $rootScope.$broadcast('siteAssetSelected', siteAssetId, siteAssetType);
        }
        if (vm.isClientMode && !vm.inEditMode && vm.onAssetSelected) {
            vm.onAssetSelected({asset: siteAsset});
        }
    }

    function onResize() {
        if (vm.aspect > 0 || (vm.site && vm.site.useGoogleMaps)) {
            var width = siteMapContainer.width();
            if (width > 0) {
                var height = width / vm.aspect;
                var siteMapHeight = siteMapContainer.height();
                if (siteMapHeight > 0 && height > siteMapHeight) {
                    height = siteMapHeight;
                    width = height * vm.aspect;
                }
                width *= maxZoom;
                if (vm.width !== width) {
                    vm.width = width;
                    vm.height = width / vm.aspect;
                }
            }
            if (!vm.map) {
                initMap();
            } else {
                updateBounds();
                vm.map.invalidateSize(true);
                updateMarkers();
            }
        }
    }

    function loadSite(currentSiteAssetId) {
        vm.site = null;
        if (vm.mode === 'sites' || vm.clientParentAssetId) {
            vm.loading = true;
            var sitePromise;
            if (vm.mode === 'sites') {
                sitePromise = siteService.getSystem();
            } else if (vm.mode === 'site'){
                sitePromise = siteService.getSite(vm.clientParentAssetId);
            } else if (vm.mode === 'siloGroup'){
                sitePromise = siloGroupService.getSiloGroup(vm.clientParentAssetId);
            }
            sitePromise.then(
                function success(site) {
                    vm.site = site;
                    if (vm.isClientMode && !vm.hasParentAsset) {
                        if (vm.mode === 'sites') {
                            vm.ctx.widgetTitle = $translate.instant('ag.system.system') + '';
                        } else {
                            vm.ctx.widgetTitle = vm.site.label || vm.site.name;
                        }
                    }
                    if(vm.mode === 'sites') {
                        if(vm.site.useGoogleMaps) {
                            onResize();
                            reloadSiteAssets(false, currentSiteAssetId);
                            vm.loading = false;
                        } else if(!vm.site.imageUrl) {
                            vm.ctx.currentWidgetType = agTypes.assetBrowserWidgetType.dashboard;
                        } else {
                            testImage.src = vm.site.imageUrl;
                            reloadSiteAssets(false, currentSiteAssetId);
                        }
                    } else {
                        if(!vm.site.imageUrl) {
                            vm.ctx.currentWidgetType = agTypes.assetBrowserWidgetType.dashboard;
                        } else {
                            testImage.src = vm.site.imageUrl;
                            reloadSiteAssets(false, currentSiteAssetId);
                        }
                    }
                },
                function fail() {
                    vm.loading = false;
                }
            );
        }
    }

    function reloadSiteAssets(keepSelection, currentSiteAssetId) {
        vm.connectedSystemsObj = {};
        vm.subscribedConnectedSystems = [];
        if (vm.mode === 'sites' || vm.clientParentAssetId || vm.hasParentAsset) {
            vm.loading = true;
            vm.allSiteAssets.length = 0;
            vm.siteAssets.length = 0;
            if (!keepSelection) {
                vm.activeSiteAsset = null;

                // NOTE: Reset zoom flag so the map will be zoomed automatically to the fit the new assets.
                vm.zoomInitialized = false;
            }
            var siteMapAssetsPromise;
            if (vm.hasParentAsset) {
                siteMapAssetsPromise = vm.fetchMapAssets({fetchAlarmsSeverity: vm.isClientMode});
            } else if (vm.mode === 'sites') {
                siteMapAssetsPromise = siteService.getSystemMapSites(vm.isClientMode);
            } else if (vm.mode === 'site') {
                siteMapAssetsPromise = siteService.getSiteMapAssets(vm.clientParentAssetId, vm.isClientMode);
            } else if (vm.mode === 'siloGroup') {
                siteMapAssetsPromise = siloGroupService.getSiloGroupMapAssets(vm.clientParentAssetId, vm.isClientMode);
            }
            if (siteMapAssetsPromise) {
                siteMapAssetsPromise.then(
                    function success(allSiteAssets) {
                        vm.allSiteAssets = allSiteAssets;
                        updateSiteAssets(currentSiteAssetId);
                        vm.loading = false;
                    },
                    function fail() {
                        vm.allSiteAssets = [];
                        updateSiteAssets();
                        vm.loading = false;
                    }
                );
            } else {
                vm.allSiteAssets = [];
                updateSiteAssets();
                vm.loading = false;
            }
        }
    }

    function updateSiteAssets(currentSiteAssetId) {
        var result;
        if(!vm.site.useGoogleMaps) {
            result = $filter('filter')(vm.allSiteAssets, {positionX: '!!'});
            vm.siteAssets = $filter('filter')(result, {positionY: '!!'});
        } else {
            result = $filter('filter')(vm.allSiteAssets, {latitude: '!!'});
            vm.siteAssets = $filter('filter')(result, {longitude: '!!'});
        }
        if (currentSiteAssetId) {
            result = $filter('filter')(vm.siteAssets, {id: { id: currentSiteAssetId.id } }, true);
            if (result && result.length) {
                vm.activeSiteAsset = result[0];
            }
        }
        updateMarkers();
    }

    function getAssetsWithoutLocation() {
        return vm.allSiteAssets.filter(a => {
            const missingLocation = !vm.site?.useGoogleMaps
                ? (!a.positionX || !a.positionY)
                : (!a.latitude || !a.longitude);

            const isAeration = a.type === 'aeration';
            // NOTE: Aeration is always attached to the connected silo, they should not have adjustable position.
            return missingLocation && !isAeration;
        });
    }

    function addSiteAsset($event, disableClose = false) {
        if ($event) {
            $event.stopPropagation();
        }

        if (vm?.site?.useGoogleMaps) {
            getCurrentLocation();
        }

        const availableSiteAssets = getAssetsWithoutLocation();

        $mdDialog.show({
            controller: AddSiteMapAssetController,
            controllerAs: 'vm',
            templateUrl: addSiteMapAssetTemplate,
            parent: angular.element($document[0].body),
            locals: {
                siteAssets: availableSiteAssets ?? [],
                disableClose: disableClose
            },
            fullscreen: true,
            targetEvent: $event
        }).then(function (siteAsset) {
            if(!vm.site.useGoogleMaps) {
                siteAsset.positionX = 0.5;
                siteAsset.positionY = 0.5;
            } else {
                if (vm?.currentPosition?.latitude && vm?.currentPosition?.longitude) {
                    siteAsset.latitude = vm?.currentPosition?.latitude;
                    siteAsset.longitude = vm?.currentPosition?.longitude;
                } else {
                    siteAsset.latitude = 0;
                    siteAsset.longitude = 0;
                }
            }
            saveSiteAsset(siteAsset).then(
                function success() {
                    vm.zoomInitialized = false;
                    updateSiteAssets();
                }
            );
        }, function () {
        });
    }

    function removeSiteAsset($event, siteAsset) {
        $event.stopPropagation();
        var toSave = angular.copy(siteAsset);
        if(!vm.site.useGoogleMaps) {
            toSave.positionX = null;
            toSave.positionY = null;
        } else {
            toSave.latitude = null;
            toSave.longitude = null;
        }
        saveSiteAsset(toSave).then(
            function success() {
                if(!vm.site.useGoogleMaps) {
                    siteAsset.positionX = null;
                    siteAsset.positionY = null;
                } else {
                    siteAsset.latitude = null;
                    siteAsset.longitude = null;
                }
                updateSiteAssets();
            }
        );
    }

    function saveSiteAsset(siteAsset) {
        var deferred = $q.defer();
        var saveSiteAssetPromise = siteService.saveAssetPosition(siteAsset);
        if (saveSiteAssetPromise) {
            saveSiteAssetPromise.then(
                function success(siteAsset) {
                    var result = $filter('filter')(vm.allSiteAssets, {id: { id: siteAsset.id.id } }, true);
                    if (result && result.length) {
                        var prevSiteAsset = result[0];
                        var index = vm.allSiteAssets.indexOf(prevSiteAsset);
                        vm.allSiteAssets[index] = siteAsset;
                        deferred.resolve();
                    } else {
                        deferred.reject();
                    }
                    $rootScope.$broadcast('siteAssetPositionUpdated', siteAsset , vm.site);
                },
                function fail() {
                    deferred.reject();
                }
            );
        } else {
            deferred.reject();
        }
        return deferred.promise;
    }

    function siteAssetPin(siteAsset) {
        return agUtils.getAssetPin(siteAsset.highestAlarmSeverity, siteAsset.highestAlarmStatus);
    }

    function siteAssetIcon(siteAsset) {
        return agUtils.getAssetIcon(siteAsset.type, siteAsset.highestAlarmSeverity, siteAsset.highestAlarmStatus);
    }

    function siteAssetTooltipColor(siteAsset) {
        if (!siteAsset.highestAlarmSeverity) {
            return agTypes.colors.normalColor;
        } else {
            if (siteAsset.highestAlarmStatus === types.alarmStatus.activeAck) {
                return agTypes.colors.normalGreyColor;
            } else if (siteAsset.highestAlarmSeverity === 'CRITICAL') {
                return agTypes.colors.errorColor;
            } else {
                return agTypes.colors.hasAlarmsColor;
            }
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }
    function onDataUpdated(subscription, apply) {
        vm.siteAssets.forEach((siteAsset) => {
            if (siteAsset.connectedSystems) {
                var markerPopupUdated = false;
                siteAsset.connectedSystems.forEach((system) => {
                    if (subscription.datasources[0].entityId === system.id.id && subscription.data[0].data.length) {
                        system.aerationState = subscription.data[0].data[0][1];
                        markerPopupUdated = true;
                    }
                });
                if (markerPopupUdated) {
                    vm.connectedSystemsObj[siteAsset.id.id] = siteAsset.connectedSystems;
                    updateSiteAssetMarkerPopup(siteAsset);
                }
            }
        });

        if (apply) {
            $scope.$digest();
        }
    }

    function updateSiteAssetMarkerPopup(siteAsset) {
        let popupTpl = '<div class="connected-systems-container" layout="column" style="top:' + 22*siteAsset.connectedSystems.length + 'px">' +
            '<md-button ng-repeat="connectedSystem in vm.connectedSystemsObj[\'' + siteAsset.id.id + '\'] | orderBy:\'name\'" ' +
            'class="md-icon-button" aria-label="Select connected system" style="border: 2px solid" ng-style="{\'border-color\': vm.siteAssetTooltipColor(connectedSystem)}" ng-click="vm.onSiteAssetMouseDown(connectedSystem)">' +
            '<md-icon ng-class="vm.getAssetStateAnimation(connectedSystem, connectedSystem.aerationState)" aria-label="Select connected system" md-svg-icon="{{vm.getAssetStateIcon(connectedSystem)}}" ng-style="{\'fill\': vm.siteAssetTooltipColor(connectedSystem)}"></md-icon>' +
            '<md-tooltip md-direction="top">' +
            '{{ connectedSystem.label || connectedSystem.name }}' +
            '</md-tooltip>' +
            '</md-button></div>';
        let popupElement = angular.element(popupTpl);
        $compile(popupElement)($scope);
        vm.siteAssetMarkers[siteAsset.id.id].setPopupContent(popupElement[0]);
    }

    function getAssetStateIcon(connectedSystem) {
        if (connectedSystem.type === agTypes.assetType.aeration) {
            if (connectedSystem.aerationType === agTypes.aerationType.alarm) {
                return 'ag:aeration_alarm';
            } else {
                return 'ag:aeration';
            }
        }
    }

    function getAssetStateAnimation(connectedSystem, state) {
        if (connectedSystem.type === agTypes.assetType.aeration) {
            if (connectedSystem.aerationType === agTypes.aerationType.alarm) {
                if (state === 'on') {
                    return 'aerationBlink';
                } else {
                    return '';
                }
            } else {
                if (state === 'on') {
                    return 'aerationRotate';
                } else {
                    return '';
                }
            }
        }
    }
}

/*@ngInject*/
function AddSiteMapAssetController($scope, $mdDialog, siteAssets, disableClose) {

    var vm = this;

    vm.siteAssets = siteAssets;
    vm.disableClose = disableClose;
    vm.siteAsset = null;

    vm.add = add;
    vm.cancel = cancel;

    function cancel() {
        $mdDialog.cancel();
    }

    function add() {
        $scope.theForm.$setPristine();
        $mdDialog.hide(vm.siteAsset);
    }
}
